import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Card, EmptyState, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { Clock, CheckCircle2, XCircle, Building2, FileCheck2, FileWarning, Download, ChevronDown, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";

const fmt = fmtDate;
const DECISION_LABEL = { pending: "Pending", yes: "Yes", no: "No" };

function csv(rows) {
  const cols = ["state", "facility_name", "decision", "student_name", "discipline", "school_name",
    "rotation_start", "rotation_end", "ci_name", "ci_email", "placement_status", "clearance_status", "denial_reason"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}

function StateBlock({ s, rows, onOpen }) {
  const [open, setOpen] = useState(false);
  const [facOpen, setFacOpen] = useState(null);
  return (
    <div className="border rounded-xl bg-white" data-testid={`am-state-${s.state}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-5 py-4 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
        <span className="font-semibold text-zinc-900 w-10">{s.state}</span>
        <span className="text-xs text-zinc-400">{s.facilities.length} site{s.facilities.length === 1 ? "" : "s"}</span>
        <span className="ml-auto flex items-center gap-4 text-sm">
          <span className="text-amber-600" data-testid={`am-state-pending-${s.state}`}>{s.pending} pending</span>
          <span className="text-cc-green">{s.yes} yes</span>
          <span className="text-zinc-500">{s.no} no</span>
          <span className="text-xs text-zinc-400">{s.cleared} cleared · {s.not_cleared} not · {s.open_slots} slots</span>
        </span>
      </button>
      {open && (
        <div className="px-5 pb-4 space-y-2">
          {s.facilities.map((f) => {
            const fRows = rows.filter((r) => r.facility_id === f.facility_id);
            const isOpen = facOpen === f.facility_id;
            return (
              <div key={f.facility_id} className="border rounded-lg" data-testid={`am-facility-${f.facility_id}`}>
                <button onClick={() => setFacOpen(isOpen ? null : f.facility_id)} className="w-full flex flex-wrap items-center gap-3 px-4 py-2.5 text-left text-sm">
                  <span className="font-medium text-zinc-800">{f.name}</span>
                  {f.city && <span className="text-xs text-zinc-400">{f.city}</span>}
                  {f.dor_name && <span className="text-xs text-zinc-400">DOR: {f.dor_name}</span>}
                  <span className="ml-auto flex items-center gap-3 text-xs">
                    <span className="text-amber-600">{f.pending} pending</span>
                    <span className="text-cc-green">{f.yes} yes</span>
                    <span className="text-zinc-500">{f.no} no</span>
                    <span className="text-zinc-400">{f.cleared} cleared · {f.not_cleared} not · {f.open_slots} slots</span>
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-3">
                    {fRows.length === 0 ? <p className="text-xs text-zinc-400 py-2">No requests at this site yet.</p> : (
                      <table className="w-full text-sm">
                        <thead className="text-zinc-500 text-xs uppercase">
                          <tr>
                            <th className="text-left py-2">Student</th><th className="text-left py-2">School</th>
                            <th className="text-left py-2">Rotation</th><th className="text-left py-2">CI</th>
                            <th className="text-left py-2">Decision</th><th className="text-left py-2">Paperwork</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fRows.map((r) => (
                            <tr key={r.response_id || r.pr_id} className="border-t" data-testid={`am-row-${r.pr_id}`}>
                              <td className="py-2">
                                <button className="font-medium text-cc-blue hover:underline" onClick={() => onOpen(r)} data-testid={`am-open-${r.pr_id}`}>{r.student_name || "—"}</button>
                                <div className="text-xs text-zinc-400">{r.discipline}</div>
                              </td>
                              <td className="py-2 text-zinc-600">{r.school_name || "—"}</td>
                              <td className="py-2 text-xs font-mono">{fmt(r.rotation_start)} → {fmt(r.rotation_end)}</td>
                              <td className="py-2 text-zinc-600">{r.ci_name || "—"}<div className="text-xs text-zinc-400">{r.ci_email}</div></td>
                              <td className="py-2"><StatusBadge status={DECISION_LABEL[r.decision]} /></td>
                              <td className="py-2 text-xs">{r.cleared ? <span className="text-cc-green font-medium">Cleared</span> : <span className="text-amber-600">{r.clearance_status || "Not started"}</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get("/am-reports").then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data?.rows || [];
    return (data?.rows || []).filter((r) => [r.student_name, r.facility_name, r.school_name, r.ci_name, r.state]
      .filter(Boolean).join(" ").toLowerCase().includes(term));
  }, [data, q]);
  if (!data) return null;
  const t = data.totals;
  const download = () => {
    const blob = new Blob([csv(rows)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `campus-connections-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  const visibleStates = data.states.filter((s) => !q.trim() || rows.some((r) => r.state === s.state));

  return (
    <div>
      <PageHeader title="Reports" subtitle={`Placement decisions across your ${t.facilities} site${t.facilities === 1 ? "" : "s"} in ${t.states} state${t.states === 1 ? "" : "s"}`} />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6" data-testid="am-totals">
        <StatCard label="Pending" value={t.pending} icon={Clock} tone="orange" />
        <StatCard label="Answered Yes" value={t.yes} icon={CheckCircle2} tone="green" />
        <StatCard label="Answered No" value={t.no} icon={XCircle} />
        <StatCard label="Cleared" value={t.cleared} icon={FileCheck2} tone="green" />
        <StatCard label="Not Cleared" value={t.not_cleared} icon={FileWarning} tone="red" />
        <StatCard label="Open Slots" value={t.open_slots} icon={Building2} tone="blue" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search student, site, school or instructor…" className="pl-9" data-testid="am-search" />
        </div>
        <span className="text-sm text-zinc-500" data-testid="am-row-count">{rows.length} request{rows.length === 1 ? "" : "s"}</span>
        <Button variant="outline" onClick={download} data-testid="am-export"><Download className="w-4 h-4 mr-1.5" />Export CSV</Button>
      </div>

      {visibleStates.length === 0 ? (
        <Card><EmptyState text={data.scope.facility_ids.length === 0
          ? "No sites are assigned to you yet — ask your Campus Coordinator to add your facilities or states."
          : "Nothing matches your search."} /></Card>
      ) : (
        <div className="space-y-3">
          {visibleStates.map((s) => <StateBlock key={s.state} s={s} rows={rows} onOpen={setDetail} />)}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="am-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.student_name || "Student"}</DialogTitle>
            <DialogDescription>{detail?.discipline} · {detail?.school_name}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              {[["Site", `${detail.facility_name || "—"} (${detail.state})`],
                ["Decision", DECISION_LABEL[detail.decision]],
                ["Rotation", `${fmt(detail.rotation_start)} → ${fmt(detail.rotation_end)}`],
                ["Clinical Instructor", detail.ci_name ? `${detail.ci_name}${detail.ci_email ? ` · ${detail.ci_email}` : ""}` : "Not assigned"],
                ["Placement status", detail.placement_status || "—"],
                ["Paperwork", detail.cleared ? "Cleared to start" : (detail.clearance_status || "Not started")],
                ["Reason given", detail.denial_reason || ""]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex gap-3 border-b pb-1.5">
                  <span className="text-zinc-400 w-40 shrink-0">{k}</span>
                  <span className="text-zinc-800 font-medium">{v}</span>
                </div>
              ))}
              <p className="text-xs text-zinc-400 pt-2">Area Manager access is read-only. Ask the Campus Coordinator for changes.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
